// External Intake & Collection v1.0 PR 7 — Loan-Administrator staging
// review queue. Lists every StagedSubmission AwaitingReview with its scan
// status + AI assessment status. Clicking through opens the per-submission
// review view where the LA approves and files (or returns with comments).
//
// Gated by Collection.Staging.ReviewAndFile (B1 + Admin).

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Archive, ArchiveRestore, Info, Loader2, Send, ShieldAlert, ShieldCheck, ShieldHalf, ShieldQuestion, Users } from "lucide-react";
import { Badge, Input } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { FilterToolbar } from "@/components/layout/FilterToolbar";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { LoadMoreFooter } from "@/components/layout/LoadMoreFooter";
import { RowActions, type RowAction } from "@/components/ux/RowActions";
import { StagedSubmissionReviewDrawer } from "@/components/Collection/StagedSubmissionReviewDrawer";
import { ManageAssigneesModal } from "@/components/Collection/ManageAssigneesModal";
import { InternalUserPicker } from "@/components/users/InternalUserPicker";
import { useOptionalAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import {
  calendarDaysUntil,
  formatCalendarDate,
  isPastCalendarDay,
  isWithinCalendarDays,
} from "@/design-system/lib/format";
import {
  collectionStagingApi,
  type StagedSubmissionListItem,
  type ScanStatus,
  type AssessmentRunStatus,
  type StagedReviewStatus,
  type StagedSubmissionSortKey,
} from "@/api/collectionStaging";
import { collectionPackagesApi } from "@/api/collectionPackages";
import { fallbackOwnerLabel, ownerTypeLabel, useOwnerLabels } from "@/api/ownerLabels";
import { fallbackUserLabel, useInternalUserLabels } from "@/api/internalUsers";
import { useToast } from "@/components/ui/Toast";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { PACKAGE_STATUS, reviewRowStatus } from "@/components/Collection/collectionStatusLabels";

interface ScanStatusBadgeProps {
  status: ScanStatus;
  // FL-12 — when set, the Clean status came from a manual LA override
  // rather than the AV scanner. Render a distinct amber badge so the
  // queue reviewer can tell the two apart at a glance.
  overriddenAt?: string | null;
  overriddenBy?: string | null;
}

function ScanStatusBadge({ status, overriddenAt, overriddenBy }: ScanStatusBadgeProps) {
  const { dateTime } = useDateFormatter();
  if (status === "Clean" && overriddenAt) {
    const tooltip = `Scan manually overridden${
      overriddenBy ? ` by ${overriddenBy}` : ""
    } on ${dateTime(overriddenAt)}.`;
    return (
      <Badge status="review" title={tooltip}>
        <ShieldHalf size={12} className="mr-1 inline" /> Scan overridden
      </Badge>
    );
  }
  switch (status) {
    case "Clean":
      return (
        <Badge status="finalized">
          <ShieldCheck size={12} className="mr-1 inline" /> Clean
        </Badge>
      );
    case "Infected":
      return (
        <Badge status="archived">
          <ShieldAlert size={12} className="mr-1 inline" /> Infected
        </Badge>
      );
    case "Error":
      return <Badge status="info">Scan error</Badge>;
    case "Pending":
    default:
      return (
        <Badge status="info">
          <ShieldQuestion size={12} className="mr-1 inline" /> Scanning…
        </Badge>
      );
  }
}

// Review loop — the owner-approved lifecycle STATUS (L1–L6) from the shared
// staff label map; "Filed" / "Not filed" is part of the Approved label.
function ReviewStatusBadge({ row }: { row: StagedSubmissionListItem }) {
  const st = reviewRowStatus(row);
  return <Badge status={st.tone}>{st.label}</Badge>;
}

function AssessmentBadge({ status }: { status: AssessmentRunStatus }) {
  switch (status) {
    case "Completed":
      return <Badge status="finalized">AI ready</Badge>;
    case "Failed":
      return <Badge status="archived">AI failed</Badge>;
    case "Skipped":
      return <Badge status="info">No AI prompt</Badge>;
    case "Pending":
    default:
      return (
        <Badge status="info">
          <Loader2 size={12} className="mr-1 inline animate-spin" /> AI running
        </Badge>
      );
  }
}

// PROB-G (G1) — the queue row shows the first assignee's first name (+N
// overflow) to keep the column compact; the full set stays available on
// hover via the title attribute.
function firstName(displayName: string): string {
  return displayName.trim().split(/\s+/)[0] || displayName;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ⚠️ DELIBERATELY STILL AN INSTANT COMPARISON, and not an oversight — do not
// "finish" the file by converging this to `isPastCalendarDay` on its own. Its
// only caller is the badge on the "Overdue only" toggle, whose authoritative
// counterpart is the SERVER predicate behind that toggle
// (`CollectionPackageService.ListStagedAsync`, `r.DueDate < now`). Moving the
// client half alone would make the badge disagree with the set clicking it
// fetches — trading one drift for another. The two have to move together, in a
// change that can touch the backend. The row COLOUR and the overdue COUNT below
// are a different pair: both are rendered client-side in one cell, so they were
// converged here.
function isOverdue(due: string | null): boolean {
  if (!due) return false;
  return new Date(due).getTime() < Date.now();
}

type StatusFilter = "" | StagedReviewStatus;

// External Portal v2 PR 2 PROB-25c — queue time-awareness thresholds, kept in
// one place so tuning the gray/amber/red bands is a one-line change. A row
// fires the most severe band that ANY one signal triggers.
const SEVERITY = {
  dueApproachingDays: 3, // amber when within N days of the due date
  reviewWaitAmberDays: 3,
  reviewWaitRedDays: 7,
  requestAgeAmberDays: 7,
  requestAgeRedDays: 14,
} as const;

type Severity = "gray" | "amber" | "red";

const SEVERITY_TEXT: Record<Severity, string> = {
  gray: "text-onyx",
  amber: "text-status-warning",
  red: "text-status-error font-semibold",
};

// 🔴 THE DUE-DATE BANDS AND THE OVERDUE COUNT MUST SHARE ONE RULE, because
// they render in the SAME CELL — `DueChip` paints itself from this severity and
// prints the count beside it. Both were instant arithmetic against a
// `packageDueDate` that is a wall-calendar `DateOnly` (widened server-side to
// UTC midnight), so from 17:00 in Phoenix a package due TODAY went red and
// claimed "overdue 1d" on the day it was actually due.
//
// The two bands had to move TOGETHER, not just the overdue one: they partition
// the due-date axis, so converging `overdue` alone would drop a due-today row
// out of red without `approaching` catching it — it would fall through to gray,
// a new hole where the old rule at least erred loudly.
function rowSeverity(s: StagedSubmissionListItem): Severity {
  const due = s.packageDueDate;
  const overdue = isPastCalendarDay(due);
  const approaching = isWithinCalendarDays(due, SEVERITY.dueApproachingDays);
  const wait = s.reviewWaitDays ?? null;
  const age = s.daysSinceSent ?? null;

  if (
    overdue ||
    (wait != null && wait > SEVERITY.reviewWaitRedDays) ||
    (age != null && age > SEVERITY.requestAgeRedDays)
  )
    return "red";
  if (
    approaching ||
    (wait != null && wait > SEVERITY.reviewWaitAmberDays) ||
    (age != null && age > SEVERITY.requestAgeAmberDays)
  )
    return "amber";
  return "gray";
}

// PROB-25a / §6 — the two ages (request age "Sent Xd" from the package
// SentAt, and review wait "Wait Yd", time on staff's plate) collapsed into a
// tooltip on the Due cell so the queue frees a whole column.
function ageTooltip(s: StagedSubmissionListItem): string {
  const sent = s.daysSinceSent;
  const wait = s.reviewWaitDays;
  if (sent == null && wait == null) return "No age data yet";
  return `Sent ${sent != null ? `${sent}d` : "—"} · Wait ${
    wait != null ? `${wait}d` : "—"
  }`;
}

// PROB-25c / §6 — due date + relative overdue tag, severity-coloured, with
// the request/wait ages folded into the title tooltip (the old Age column).
// 🔴 IT TAKES NO FORMATTER. It used to accept `format` and the call site
// passed `useDateFormatter().date`, so the DATE HALF of this cell was rendered
// in the reader's zone while the overdue half was already on the calendar rule
// — a Honolulu reader (UTC-10) saw a package due Sep 3 printed as "Sep 2", one
// row above an overdue count that disagreed. `packageDueDate` has NO instant
// writer (`CollectionPackageService:145` and `:1230` both widen a `DateOnly` to
// midnight UTC), so there is no reader-zone question to answer here at all.
// Taking the formatter away is what makes passing the wrong one impossible; the
// unused `tz` prop went with it.
function DueChip({
  s,
  severity,
}: {
  s: StagedSubmissionListItem;
  severity: Severity;
}) {
  const due = s.packageDueDate;
  const title = ageTooltip(s);
  if (!due)
    return (
      <time className="text-caption text-gray-mid italic" title={title}>
        —
      </time>
    );
  // The negation of the countdown, on the same rule that decided `severity` —
  // so the tag can never contradict the colour it is printed in. Was
  // `Math.floor((Date.now() - due) / DAY_MS)`: written with the clock as the
  // MINUEND, which reads as an elapsed age and is why the repo-wide countdown
  // scanner (`deadlineCountdownsShareOneRule`) never flagged it, but an overdue
  // count is a countdown run backwards and belongs on the calendar rule.
  const overdueDays = -(calendarDaysUntil(due) ?? 0);
  return (
    <time
      dateTime={due}
      className={`text-small ${SEVERITY_TEXT[severity]}`}
      title={title}
    >
      {formatCalendarDate(due)}
      {overdueDays > 0 ? ` · overdue ${overdueDays}d` : ""}
    </time>
  );
}

function PackageStatePill({ status }: { status: StagedSubmissionListItem["packageStatus"] | undefined }) {
  if (status !== "Archived" && status !== "Cancelled" && status !== "Completed") return null;
  const pkg = PACKAGE_STATUS[status];
  return <Badge status={pkg.tone}>{pkg.label}</Badge>;
}

export function CollectionStagingQueuePage() {
  useBreadcrumbs([{ label: "External Collection" }, { label: "Review Queue" }]);
  const queryClient = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  // §2/§3 — the canonical review surface is a right-side slide-over opened
  // from a queue row. The open submission is mirrored to ?submissionId so
  // email + package-detail deep links auto-open it, and closing returns to
  // the queue (filters/sort are component state, so they survive intact).
  const reviewingId = params.get("submissionId");
  const openReview = (s: StagedSubmissionListItem) => {
    const next = new URLSearchParams(params);
    next.set("submissionId", s.id);
    setParams(next, { replace: true });
  };
  const closeReview = () => {
    const next = new URLSearchParams(params);
    next.delete("submissionId");
    setParams(next, { replace: true });
  };
  // PROB-D/E — the specific row whose package the user asked to archive.
  // Captured on click so the confirm modal (and the scoped archive call)
  // always act on exactly that row, never a shared mutable reference.
  const [archiveTarget, setArchiveTarget] = useState<StagedSubmissionListItem | null>(null);
  // PROB-F — the archived row the user asked to restore.
  const [unarchiveTarget, setUnarchiveTarget] = useState<StagedSubmissionListItem | null>(null);
  // PROB-C — the row pending a reminder-send confirmation, plus the package
  // id that just sent (so its button can flash a brief "Sent" state).
  const [remindTarget, setRemindTarget] = useState<StagedSubmissionListItem | null>(null);
  const [recentlyRemindedId, setRecentlyRemindedId] = useState<string | null>(null);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [includeReviewed, setIncludeReviewed] = useState(false);
  // Bugfix #21 — explicit status filter so staff can isolate the
  // "Returned" pile without scrolling past every approval. Empty string
  // means "no explicit filter" → defer to includeReviewed.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  // FL-8 — server-side search across the recipient's name + email plus a
  // simple sort selector. Owner-entity-type drop-down is a free-text filter
  // because owner ids are GUIDs; staff who land here from a deal pin the
  // owner type via the existing controller params.
  // §1 — the account page hands off here via ?recipient={email}; seed the
  // search box from it once on mount.
  const [search, setSearch] = useState(() => params.get("recipient") ?? "");
  const [ownerEntityType, setOwnerEntityType] = useState("");
  // PROB-25c — default to the time-aware "Urgency" sort: overdue first →
  // approaching due → newest submission. The server ignores the direction
  // flag for this key.
  const [sortBy, setSortBy] = useState<StagedSubmissionSortKey>("Urgency");
  const [sortDescending, setSortDescending] = useState(true);
  // PR 3 §10.5 — default-hide cancelled/archived packages, mirroring the
  // existing Include-reviewed toggle.
  const [includeArchivedPackages, setIncludeArchivedPackages] = useState(false);
  // PR 3 §10.1 — Assigned-to-me view + deal-role narrowing.
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [dealAssignmentRoleCode, setDealAssignmentRoleCode] = useState("");
  // PROB-G (G3) — staff-member assignee filter. Blank means no filter; the
  // "Assigned to me" checkbox remains as a quick shortcut. Backed by the
  // queue endpoint's existing assignedToUserId query param.
  const [assignedToUserId, setAssignedToUserId] = useState("");
  // PROB-G (G2) — package whose Manage Assignees modal is open, or null. The
  // modal is the same control the detail panel surfaces inline; opening it
  // here avoids drilling into the detail page to (re)assign a row.
  const [manageAssigneesPackageId, setManageAssigneesPackageId] = useState<
    string | null
  >(null);
  // PROB-G (G2) — only package managers may edit assignees. Optional auth so
  // the page still renders in tests that don't bootstrap an AuthProvider.
  const auth = useOptionalAuth();
  const canManageAssignees =
    auth?.hasPermission("Collection.ManagePackages") ?? false;

  // UAT FixBatch-06 FIND-32 — server-side pagination via useInfiniteQuery. The
  // queryKey carries every filter/sort, so changing any of them is a distinct
  // query that starts at page 1 — the next page of new-filter rows is never
  // appended onto the old filter's list. useInfiniteQuery (rather than the
  // shared usePaginatedList hook) is used here so the existing
  // invalidateQueries(["collection-staging"]) calls after archive / unarchive /
  // remind keep refetching every loaded page in place.
  const PAGE_SIZE = 50;
  const list = useInfiniteQuery({
    queryKey: [
      "collection-staging",
      {
        overdueOnly,
        includeReviewed,
        statusFilter,
        search,
        ownerEntityType,
        sortBy,
        sortDescending,
        includeArchivedPackages,
        assignedToMe,
        dealAssignmentRoleCode,
        assignedToUserId,
      },
    ],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      collectionStagingApi.list({
        overdueOnly,
        includeReviewed,
        reviewStatus: statusFilter || undefined,
        search: search.trim() || undefined,
        ownerEntityType: ownerEntityType.trim() || undefined,
        sortBy,
        sortDescending,
        includeArchivedPackages,
        assignedToMe: assignedToMe || undefined,
        dealAssignmentRoleCode: dealAssignmentRoleCode.trim() || undefined,
        assignedToUserId: assignedToUserId || undefined,
        offset: pageParam,
        limit: PAGE_SIZE,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    // Live triage queue — opt out of the 5-minute global staleTime.
    staleTime: 60_000,
  });

  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  const total = list.data?.pages[0]?.total ?? 0;

  // PROB-25c — count of overdue rows in the current view, surfaced as a
  // badge on the "Overdue only" toggle.
  const overdueCount = rows.filter((r) => isOverdue(r.packageDueDate)).length;

  // PR 3 §10.3 / §10.5 — per-package archive + on-demand reminder actions.
  // Both invalidate the queue + any per-package detail caches.
  const archiveMutation = useMutation({
    mutationFn: (packageId: string) => collectionPackagesApi.archive(packageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collection-staging"] });
      queryClient.invalidateQueries({ queryKey: ["collection-package"] });
      queryClient.invalidateQueries({ queryKey: ["staging-detail"] });
      toast.success("Package archived.");
    },
    onError: (e: Error) =>
      toast.error(e.message || "Failed to archive — please try again."),
  });
  // PROB-F — restore an archived package; server recomputes the active status.
  const unarchiveMutation = useMutation({
    mutationFn: (packageId: string) => collectionPackagesApi.unarchive(packageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collection-staging"] });
      queryClient.invalidateQueries({ queryKey: ["collection-package"] });
      queryClient.invalidateQueries({ queryKey: ["staging-detail"] });
      toast.success("Package restored.");
    },
    onError: (e: Error) =>
      toast.error(e.message || "Failed to restore — please try again."),
  });
  const remindMutation = useMutation({
    mutationFn: (packageId: string) => collectionPackagesApi.remind(packageId),
    onSuccess: (_data, packageId) => {
      queryClient.invalidateQueries({ queryKey: ["collection-staging"] });
      queryClient.invalidateQueries({ queryKey: ["collection-package"] });
      const name = rows.find((r) => r.packageId === packageId)?.recipientName ?? "recipient";
      toast.success(`Reminder sent to ${name}.`);
      // PROB-C — flash a brief "Sent" state on the row's button, then clear.
      setRecentlyRemindedId(packageId);
      window.setTimeout(
        () => setRecentlyRemindedId((cur) => (cur === packageId ? null : cur)),
        2000,
      );
    },
    onError: (e: Error) =>
      toast.error(e.message || "Failed to send reminder — please try again."),
  });

  // PROB-04 — resolve the OWNER GUIDs across every queue row.
  const owners = useOwnerLabels(
    rows.map((s) => ({ type: s.ownerEntityType, id: s.ownerEntityId })),
  );

  // PROB-24a — resolve every ASSIGNEE id across the visible rows in one
  // batched lookup so the "Assigned to" column renders names, not GUIDs.
  const staff = useInternalUserLabels(
    rows.flatMap((s) => {
      const ids = [...(s.assigneeUserIds ?? [])];
      if (ids.length === 0 && s.effectiveAssignedToUserId) {
        ids.push(s.effectiveAssignedToUserId);
      }
      return ids;
    }),
  );

  const columns: Column<StagedSubmissionListItem>[] = [
    {
      key: "file",
      header: "Submission",
      // §2 — clicking the row (DataTable.onRowClick) opens the review
      // slide-over. §6 — the document tag rides inline as a small chip here
      // instead of owning its own column.
      render: (s) => (
        <div className="flex flex-col">
          <span className="flex items-center gap-space-2 font-semibold text-onyx">
            {s.templateName}
            {s.documentTagCode && (
              <span className="rounded-full border border-border-light bg-white-warm px-space-2 py-[1px] text-caption font-normal text-gray-mid">
                {s.documentTagCode}
              </span>
            )}
            {s.guarantorLabel && (
              <span className="rounded-full border border-border-light bg-white-warm px-space-2 py-[1px] text-caption font-normal text-gray-mid">
                Guarantor: {s.guarantorLabel}
              </span>
            )}
          </span>
          <span className="text-caption text-gray-mid font-normal truncate max-w-md">
            {s.originalFileName} · {formatSize(s.sizeBytes)}
          </span>
        </div>
      ),
    },
    {
      key: "owner",
      header: "Owner",
      render: (s) => {
        const ref = { type: s.ownerEntityType, id: s.ownerEntityId };
        const lbl = owners.lookup(ref);
        // PROB-14 — show the human-readable owner-type label. The backend
        // resolves ExternalRecipient to the recipient's name + email; the
        // chip just needs the friendly type ("Recipient", not the internal
        // "ExternalRecipient").
        const pretty = ownerTypeLabel(s.ownerEntityType);
        return (
          <span className="text-small text-onyx">
            <span className="text-caption uppercase text-gray-mid">{pretty}</span>{" "}
            {lbl ? lbl.label : fallbackOwnerLabel(ref)}
          </span>
        );
      },
    },
    {
      key: "recipient",
      header: "Recipient",
      render: (s) => (
        <div className="flex flex-col">
          <span className="text-small text-onyx">{s.recipientName}</span>
          <span className="text-caption text-gray-mid">{s.recipientEmail}</span>
        </div>
      ),
    },
    // §6 — Status / Scan / AI keep their own columns but get a min-width +
    // nowrap so the chips never wrap or squish against each other.
    {
      key: "review",
      header: "Status",
      className: "min-w-[110px] whitespace-nowrap",
      render: (s) => <ReviewStatusBadge row={s} />,
    },
    {
      key: "scan",
      header: "Scan",
      className: "min-w-[110px] whitespace-nowrap",
      render: (s) => (
        <ScanStatusBadge
          status={s.scanStatus}
          overriddenAt={s.scanOverriddenAt}
          overriddenBy={s.scanOverriddenById}
        />
      ),
    },
    {
      key: "ai",
      header: "AI",
      className: "min-w-[110px] whitespace-nowrap",
      render: (s) => <AssessmentBadge status={s.assessmentStatus} />,
    },
    {
      key: "due",
      header: "Due",
      // PROB-25c — date + relative overdue tag, severity-coloured. §6 — wide
      // enough for "May 26, 2026 · overdue 2d" on one line; the request/wait
      // ages now live in this cell's tooltip (the old Age column is gone).
      className: "min-w-[140px] whitespace-nowrap",
      render: (s) => (
        <DueChip s={s} severity={rowSeverity(s)} />
      ),
    },
    // PROB-24a — resolved assignee names (never raw GUIDs) + role tag. Many-
    // to-many: render the first 3 explicit assignees as name chips, then
    // "+N more"; fall back to the DealRole effective assignee (role code in
    // brackets) when there is no explicit assignee. No "(explicit)" tag.
    {
      key: "assigned",
      header: "Assigned to",
      render: (s) => {
        const explicitIds = s.assigneeUserIds ?? [];
        if (explicitIds.length > 0) {
          const names = explicitIds.map(
            (uid) => staff.lookup(uid)?.displayName ?? fallbackUserLabel(uid),
          );
          const extra = names.length - 1;
          return (
            <span className="text-small text-onyx" title={names.join(", ")}>
              {firstName(names[0])}
              {extra > 0 ? ` +${extra}` : ""}
            </span>
          );
        }
        if (s.effectiveAssignedToUserId) {
          const name =
            staff.lookup(s.effectiveAssignedToUserId)?.displayName ??
            fallbackUserLabel(s.effectiveAssignedToUserId);
          const suffix =
            s.effectiveAssignedToSource === "DealRole" && s.effectiveAssignedToRoleCode
              ? ` (${s.effectiveAssignedToRoleCode})`
              : "";
          return (
            <span className="text-small text-onyx" title={s.effectiveAssignedToSource ?? undefined}>
              {name}
              {suffix}
            </span>
          );
        }
        return <span className="text-caption text-gray-mid italic">Unassigned</span>;
      },
    },
    // PR 3 §10.5 — show the parent package's terminal state when the
    // Include-archived toggle un-hides cancelled/archived rows.
    {
      key: "pkgState",
      header: "",
      render: (s) => <PackageStatePill status={s.packageStatus} />,
    },
    // §6 — Remind / Manage Assignees / Change owner / Archive collapse into a
    // single overflow (⋯) menu so the row stays visually quiet. Reminder is
    // disabled for terminal packages (the server refuses anyway); archived
    // packages offer Restore instead of Archive.
    {
      key: "actions",
      header: "",
      className: "w-12",
      render: (s) => {
        // PROB-C / PROB-23 — disable Remind for statuses the server would
        // refuse anyway, with an explanatory helper line.
        const remindBlockedReason =
          s.packageStatus === "Draft"
            ? "Package not yet sent."
            : s.packageStatus === "Completed"
              ? "Package already completed."
              : s.packageStatus === "Cancelled" || s.packageStatus === "Archived"
                ? `Package ${s.packageStatus.toLowerCase()}.`
                : null;
        const justSent = recentlyRemindedId === s.packageId;
        const isArchived = s.packageStatus === "Archived";
        const actions: RowAction[] = [
          {
            key: "remind",
            label: justSent ? "Reminder sent" : "Send reminder",
            icon: Send,
            disabled:
              remindBlockedReason !== null || remindMutation.isPending || justSent,
            helperText: remindBlockedReason ?? undefined,
            // PROB-C — confirm before sending; the modal carries the
            // recipient name + email for this exact row.
            onClick: () => setRemindTarget(s),
          },
          {
            key: "assignees",
            label: "Manage assignees",
            icon: Users,
            hidden: !canManageAssignees,
            onClick: () => setManageAssigneesPackageId(s.packageId),
          },
          {
            key: "owner",
            label: "Change owner",
            // The retarget-owner control lives in the review slide-over;
            // opening it keeps the queue row free of a second modal.
            onClick: () => openReview(s),
          },
          isArchived
            ? {
                // PROB-F — archived rows offer Restore.
                key: "restore",
                label: "Restore package",
                icon: ArchiveRestore,
                disabled: unarchiveMutation.isPending,
                onClick: () => setUnarchiveTarget(s),
              }
            : {
                // PROB-D/E — scoped, in-app-confirmed archive.
                key: "archive",
                label: "Archive package",
                icon: Archive,
                destructive: true,
                disabled: archiveMutation.isPending,
                onClick: () => setArchiveTarget(s),
              },
        ];
        return <RowActions actions={actions} />;
      },
    },
  ];

  const selectClass =
    "h-10 rounded-md border border-border-light px-space-2 text-small bg-white-pure";

  return (
    <div className="flex flex-col gap-space-4">
      {/* PROB-22 — compact PageHeader so the H1 stays single-line at
          standard viewport widths; the filter row moves to the
          FilterToolbar below for a clear header → toolbar → table read. */}
      <PageHeader
        eyebrow="External Collection"
        title="Collection — Staging Review"
        description="Cross-account inbox — click any row to preview the file and review it in a side panel."
      />
      <FilterToolbar
        searchInput={
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Recipient name or email"
            className="w-64"
            aria-label="Search recipients"
          />
        }
        filters={[
          {
            key: "owner",
            label: "Owner",
            control: (
              <select
                value={ownerEntityType}
                onChange={(e) => setOwnerEntityType(e.target.value)}
                className={selectClass}
              >
                <option value="">All owners</option>
                <option value="Deal">Deal</option>
                <option value="Loan">Loan</option>
                <option value="Person">Person</option>
                <option value="Entity">Entity</option>
                <option value="Business">Business</option>
                <option value="Broker">Broker</option>
                <option value="Vendor">Vendor</option>
                <option value="ExternalRecipient">External recipient</option>
              </select>
            ),
          },
          {
            key: "status",
            label: "Status",
            control: (
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                className={selectClass}
              >
                <option value="">All statuses</option>
                <option value="AwaitingReview">Awaiting review</option>
                <option value="Returned">Returned</option>
                <option value="Approved">Approved</option>
              </select>
            ),
          },
          {
            key: "role",
            label: "Role",
            control: (
              <select
                value={dealAssignmentRoleCode}
                onChange={(e) => setDealAssignmentRoleCode(e.target.value)}
                className={selectClass}
                title="Filter to packages whose owner deal has a current assignee on this role"
              >
                <option value="">All roles</option>
                <option value="C1">C1 — Counterparty</option>
                <option value="B1">B1 — Loan Administrator</option>
                <option value="B2">B2 — Liaison Manager</option>
                <option value="A1">A1 — Liaison / Underwriter</option>
                <option value="A2">A2 — Analyst lane 2</option>
                <option value="A3">A3 — Analyst lane 3</option>
                <option value="A4">A4 — Analyst lane 4</option>
              </select>
            ),
          },
          {
            key: "assignedTo",
            label: "Assigned to",
            control: (
              <div className="w-64">
                <InternalUserPicker
                  value={assignedToUserId ? [assignedToUserId] : []}
                  onChange={(ids) => setAssignedToUserId(ids[0] ?? "")}
                  maxSelections={1}
                  placeholder="Filter by staff member…"
                />
              </div>
            ),
          },
          {
            key: "assignedToMe",
            control: (
              <label className="inline-flex items-center gap-space-2 text-small text-onyx h-10">
                <input
                  type="checkbox"
                  checked={assignedToMe}
                  onChange={(e) => setAssignedToMe(e.target.checked)}
                />
                Assigned to me
              </label>
            ),
          },
          {
            key: "overdueOnly",
            control: (
              <label className="inline-flex items-center gap-space-2 text-small text-onyx h-10">
                <input
                  type="checkbox"
                  checked={overdueOnly}
                  onChange={(e) => setOverdueOnly(e.target.checked)}
                />
                Overdue only ({overdueCount})
              </label>
            ),
          },
          {
            key: "includeReviewed",
            control: (
              <label className="inline-flex items-center gap-space-2 text-small text-onyx h-10">
                <input
                  type="checkbox"
                  checked={includeReviewed}
                  onChange={(e) => setIncludeReviewed(e.target.checked)}
                  disabled={statusFilter !== ""}
                />
                Include reviewed
              </label>
            ),
          },
          {
            key: "includeArchived",
            control: (
              <label className="inline-flex items-center gap-space-2 text-small text-onyx h-10">
                <input
                  type="checkbox"
                  checked={includeArchivedPackages}
                  onChange={(e) => setIncludeArchivedPackages(e.target.checked)}
                />
                Include archived
              </label>
            ),
          },
        ]}
        sort={
          <>
            <label className="text-caption uppercase tracking-label text-gold-shadow">
              Sort
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as StagedSubmissionSortKey)}
              className={selectClass}
            >
              <option value="Urgency">Overdue first</option>
              <option value="CreatedAt">Submitted</option>
              <option value="DueDate">Due date</option>
              <option value="Recipient">Recipient</option>
              <option value="Owner">Owner type</option>
            </select>
            <button
              type="button"
              onClick={() => setSortDescending((v) => !v)}
              className="h-10 px-space-2 border border-border-light rounded-md text-small text-onyx hover:bg-white-warm"
              aria-label="Toggle sort direction"
            >
              {sortDescending ? "Desc" : "Asc"}
            </button>
          </>
        }
      />
      <div className="flex gap-space-2 p-space-3 bg-white-warm border border-border-light rounded-md text-small text-gray-mid">
        <Info size={16} strokeWidth={1.5} className="text-gold-shadow shrink-0 mt-[2px]" aria-hidden />
        <div>
          Click any row to open the submission in a side panel — preview the
          file inline, read the AI distillation and scan status, then approve
          or request changes without leaving the queue.
        </div>
      </div>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(s) => s.id}
        loading={list.isLoading}
        error={list.error instanceof Error ? list.error.message : null}
        emptyMessage="No submissions awaiting review."
        // §2 — whole-row click opens the review slide-over.
        onRowClick={openReview}
        // PROB-B — roomier rows so the two-line owner/recipient content has
        // breathing room (default cells are py-space-3).
        rowClassName="[&>td]:py-space-4"
      />

      {/* UAT FixBatch-06 FIND-32 — "Showing N of Total" + Load more. */}
      {!list.isLoading && rows.length > 0 && (
        <LoadMoreFooter
          loadedCount={rows.length}
          total={total}
          loading={list.isFetchingNextPage}
          onLoadMore={() => list.fetchNextPage()}
        />
      )}

      {/* PROB-E — in-app archive confirmation, scoped to the clicked row
          (PROB-D). Replaces the old window.confirm. */}
      <ConfirmModal
        open={archiveTarget !== null}
        title="Archive this package?"
        message="It will be hidden from active queues but stays in audit history. You can restore it later from the Archived view."
        confirmLabel="Archive"
        destructive
        onConfirm={() => {
          if (archiveTarget) archiveMutation.mutate(archiveTarget.packageId);
          setArchiveTarget(null);
        }}
        onCancel={() => setArchiveTarget(null)}
      />

      {/* PROB-F — restore an archived package back to the active queue. */}
      <ConfirmModal
        open={unarchiveTarget !== null}
        title="Restore this package?"
        message="It will return to the active review queue."
        confirmLabel="Restore"
        onConfirm={() => {
          if (unarchiveTarget) unarchiveMutation.mutate(unarchiveTarget.packageId);
          setUnarchiveTarget(null);
        }}
        onCancel={() => setUnarchiveTarget(null)}
      />

      {/* PROB-C — confirm before firing an on-demand reminder email. */}
      <ConfirmModal
        open={remindTarget !== null}
        title="Send reminder?"
        message={
          remindTarget
            ? `A reminder email will be sent to ${remindTarget.recipientName} (${remindTarget.recipientEmail}) for this collection package.`
            : ""
        }
        confirmLabel="Send reminder"
        onConfirm={() => {
          if (remindTarget) remindMutation.mutate(remindTarget.packageId);
          setRemindTarget(null);
        }}
        onCancel={() => setRemindTarget(null)}
      />
      {/* PROB-G (G2) — second trigger point for Manage Assignees, scoped to
          the clicked row's package. After a save the editor invalidates the
          queue cache so the ASSIGNED TO column updates in place. */}
      {manageAssigneesPackageId && (
        <ManageAssigneesModal
          open
          packageId={manageAssigneesPackageId}
          onClose={() => setManageAssigneesPackageId(null)}
          onChanged={() =>
            queryClient.invalidateQueries({ queryKey: ["collection-staging"] })
          }
        />
      )}

      {/* §2 — the canonical review surface: a right-side slide-over rendering
          the full review panel (file viewer, AI distillation, scan, reviewer
          note, file-into-folder, action row). Closing it returns to the queue
          with filters/sort intact. Shared with the Broker detail review tab. */}
      <StagedSubmissionReviewDrawer
        submissionId={reviewingId}
        row={reviewingId ? rows.find((r) => r.id === reviewingId) ?? null : null}
        onClose={closeReview}
      />
    </div>
  );
}
